import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { db } from '../firebaseInit';
import { StatCardSkeleton } from '../components/shared/StatCard';
import { formatUserName } from '../utils/industryConfig';
import * as firestorePaths from '../utils/firestorePaths';

// ─── Viewport height hook ─────────────────────────────────────────────────────
// Returns live window.innerHeight, updates on resize.
const useViewportHeight = () => {
  const [vh, setVh] = useState(() => (typeof window !== 'undefined' ? window.innerHeight : 800));
  useEffect(() => {
    const onResize = () => setVh(window.innerHeight);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return vh;
};

// Computes chart heights clamped to reasonable min/max so charts look good on
// every screen from a 13" laptop to a 32" 4K monitor.
const useChartHeights = () => {
  const vh = useViewportHeight();
  return {
    trendH: Math.max(180, Math.min(380, Math.round(vh * 0.25))),
    funnelH: Math.max(120, Math.min(200, Math.round(vh * 0.14))),
  };
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const PERIODS = [
  { key: 'today',      label: 'Today' },
  { key: 'yesterday',  label: 'Yesterday' },
  { key: '7d',         label: 'Last 7 days' },
  { key: '30d',        label: 'Last 30 days' },
  { key: '90d',        label: 'Last 90 days' },
  { key: 'this_month', label: 'This month' },
  { key: 'last_month', label: 'Last month' },
  { key: 'all_time',   label: 'All Time' },
];

const getDateRange = (period) => {
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const endOfToday   = new Date(startOfToday.getTime() + 24 * 60 * 60 * 1000);

  switch (period) {
    case 'today':
      return { start: startOfToday, end: endOfToday };
    case 'yesterday': {
      const s = new Date(startOfToday.getTime() - 24 * 60 * 60 * 1000);
      return { start: s, end: startOfToday };
    }
    case '7d':
      return { start: new Date(now.getTime() - 7  * 24 * 60 * 60 * 1000), end: endOfToday };
    case '30d':
      return { start: new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000), end: endOfToday };
    case '90d':
      return { start: new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000), end: endOfToday };
    case 'this_month':
      return { start: new Date(now.getFullYear(), now.getMonth(), 1), end: endOfToday };
    case 'last_month': {
      const s = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const e = new Date(now.getFullYear(), now.getMonth(), 1);
      return { start: s, end: e };
    }
    case 'all_time':
      return { start: null, end: null };
    default:
      return { start: null, end: null };
  }
};

const getScoreGrade = (score) => {
  if (score === null || score === undefined) return null;
  if (score >= 80) return { label: 'Excellent', classes: 'bg-green-100 text-green-700' };
  if (score >= 65) return { label: 'Good', classes: 'bg-blue-100 text-blue-700' };
  if (score >= 50) return { label: 'Average', classes: 'bg-yellow-100 text-yellow-700' };
  return { label: 'Needs Work', classes: 'bg-red-100 text-red-700' };
};

const getConvClasses = (rate) => {
  if (rate == null) return 'bg-gray-100 text-gray-500';
  if (rate >= 35) return 'bg-green-100 text-green-700';
  if (rate >= 20) return 'bg-yellow-100 text-yellow-700';
  return 'bg-red-100 text-red-700';
};

const getRankBadgeClasses = (rank) => {
  if (rank === 1) return 'bg-amber-400 text-white';
  if (rank === 2) return 'bg-gray-400 text-white';
  if (rank === 3) return 'bg-orange-400 text-white';
  return 'bg-gray-100 text-gray-500';
};

const getAvatarClasses = (rank) => {
  if (rank === 1) return 'bg-amber-50 border border-amber-300 text-amber-700';
  if (rank === 2) return 'bg-gray-50 border border-gray-300 text-gray-600';
  if (rank === 3) return 'bg-orange-50 border border-orange-300 text-orange-700';
  return 'bg-blue-50 border border-blue-200 text-blue-600';
};

const getInitials = (name) => {
  if (!name || name === 'Unknown') return '?';
  const parts = name.trim().split(/\s+/);
  return parts.length >= 2
    ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    : parts[0][0].toUpperCase();
};

// Build sorted daily date array spanning start→today
const buildDailyTrend = (dailyStats, period) => {
  if (!dailyStats) return [];
  const { start } = getDateRange(period);
  // For all_time: determine range from the actual data rather than a fixed fallback
  let startDate;
  if (!start) {
    const keys = Object.keys(dailyStats).sort();
    startDate = keys.length > 0
      ? new Date(keys[0])
      : new Date(Date.now() - 90 * 24 * 60 * 60 * 1000);
  } else {
    startDate = start;
  }
  const endDate = new Date();
  const result = [];
  const cursor = new Date(startDate);
  while (cursor <= endDate) {
    const key = cursor.toISOString().slice(0, 10);
    const day = dailyStats[key] || {};
    result.push({
      date: cursor.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      Leads: day.leads || 0,
      'Calls Completed': day.completed || 0,
    });
    cursor.setDate(cursor.getDate() + 1);
  }
  return result;
};

// Custom tooltip for Recharts — matches app typography
const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-xs">
      <p className="font-semibold text-gray-700 mb-1">{label}</p>
      {payload.map((p) => (
        <p key={p.name} style={{ color: p.color }} className="font-medium">
          {p.name}: {p.value}
        </p>
      ))}
    </div>
  );
};

// ─── Shared UI ────────────────────────────────────────────────────────────────

const KPI_THEMES = {
  blue:   { bg: 'bg-gradient-to-br from-blue-50 to-blue-100/60',  border: 'border-blue-200', iconBg: 'bg-blue-500',  text: 'text-blue-700' },
  indigo: { bg: 'bg-gradient-to-br from-indigo-50 to-indigo-100/60', border: 'border-indigo-200', iconBg: 'bg-indigo-500', text: 'text-indigo-700' },
  emerald:{ bg: 'bg-gradient-to-br from-emerald-50 to-emerald-100/60', border: 'border-emerald-200', iconBg: 'bg-emerald-500', text: 'text-emerald-700' },
  amber:  { bg: 'bg-gradient-to-br from-amber-50 to-amber-100/60',  border: 'border-amber-200', iconBg: 'bg-amber-500',  text: 'text-amber-700' },
};

const KpiCard = ({ icon, label, value, sub, theme = 'indigo' }) => {
  const t = KPI_THEMES[theme] || KPI_THEMES.indigo;
  return (
    <div className={`${t.bg} rounded-lg border ${t.border} shadow-sm px-3 py-3 flex items-center gap-2.5`}>
      <div className={`w-8 h-8 rounded-lg ${t.iconBg} flex items-center justify-center text-sm text-white flex-shrink-0 shadow-sm`}>
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wide leading-tight">{label}</p>
        <p className={`text-lg font-bold ${t.text} leading-tight`}>{value}</p>
        {sub && <p className="text-[11px] text-gray-500 leading-tight">{sub}</p>}
      </div>
    </div>
  );
};

// All Sections stretch to h-full so siblings in a CSS grid row match heights.
const Section = ({ title, icon, action, children, loading, empty, emptyText, noPad }) => (
  <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden h-full flex flex-col">
    <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100 bg-gray-50 flex-shrink-0">
      <div className="flex items-center gap-2">
        {icon && <span className="text-base">{icon}</span>}
        <span className="text-xs font-semibold text-gray-600 uppercase tracking-wider">{title}</span>
      </div>
      {action}
    </div>
    {loading ? (
      <div className="p-3 space-y-2 flex-1">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-8 bg-gray-100 rounded animate-pulse" />
        ))}
      </div>
    ) : empty ? (
      <div className="px-3 py-8 text-center text-sm text-gray-400 flex-1">{emptyText || 'No data for this period'}</div>
    ) : noPad ? (
      <div className="flex-1 overflow-y-auto min-h-0">{children}</div>
    ) : (
      <div className="flex-1 overflow-y-auto min-h-0 p-4">{children}</div>
    )}
  </div>
);

const SectionLink = ({ label, onClick }) => (
  <button
    onClick={onClick}
    className="text-xs font-medium text-blue-600 hover:text-blue-800 transition-colors"
  >
    {label} →
  </button>
);

const PeriodSelector = ({ period, onChange }) => (
  <select
    value={period}
    onChange={(e) => onChange(e.target.value)}
    className="text-sm font-medium text-gray-700 border border-gray-300 rounded-md px-3 py-1.5 bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 cursor-pointer"
  >
    {PERIODS.map((p) => (
      <option key={p.key} value={p.key}>{p.label}</option>
    ))}
  </select>
);

// ─── Chart: Leads & Calls Trend ───────────────────────────────────────────────
// chartHeight: fixed px height for default layout; omit for customize mode (fill)
const TrendChart = ({ data, loading, chartHeight }) => (
  <Section
    title="Leads & Calls Trend"
    icon="📈"
    loading={loading}
    empty={!loading && (!data || data.every((d) => d.Leads === 0 && d['Calls Completed'] === 0))}
    emptyText="No activity data for this period"
    noPad
  >
    <div className="p-4 pt-3" style={chartHeight ? { height: chartHeight } : {}}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
          <defs>
            <linearGradient id="gradLeads" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#2563EB" stopOpacity={0.18} />
              <stop offset="95%" stopColor="#2563EB" stopOpacity={0} />
            </linearGradient>
            <linearGradient id="gradCalls" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#10B981" stopOpacity={0.15} />
              <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 11, fill: '#9CA3AF' }}
            axisLine={false}
            tickLine={false}
            interval="preserveStartEnd"
          />
          <YAxis
            tick={{ fontSize: 11, fill: '#9CA3AF' }}
            axisLine={false}
            tickLine={false}
            allowDecimals={false}
          />
          <Tooltip content={<ChartTooltip />} />
          <Legend
            iconType="circle"
            iconSize={8}
            wrapperStyle={{ fontSize: 12, paddingTop: 8 }}
          />
          <Area
            type="monotone"
            dataKey="Calls Completed"
            stroke="#10B981"
            strokeWidth={2}
            fill="url(#gradCalls)"
            dot={false}
            activeDot={{ r: 4 }}
          />
          <Area
            type="monotone"
            dataKey="Leads"
            stroke="#2563EB"
            strokeWidth={2}
            fill="url(#gradLeads)"
            dot={false}
            activeDot={{ r: 4 }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  </Section>
);

// ─── Chart: Calls Funnel (Donut) ──────────────────────────────────────────────
// Colors: Lead Captured (blue), Fully Completed (green), Not Converted (amber), Abandoned (gray)
const FUNNEL_COLORS = ['#2563EB', '#10B981', '#F59E0B', '#D1D5DB'];

// chartHeight: pixel height for the donut chart (default 180, scales with viewport)
const FunnelChart = ({ sessionOutcomes, completedCalls, totalLeads, droppedCalls, loading, chartHeight = 180, actualLeadCount }) => {
  let data;
  if (sessionOutcomes) {
    const analyticsLeads = (sessionOutcomes.completedWithLead || 0) + (sessionOutcomes.droppedWithLead || 0);
    // Use authoritative Firestore lead count when available
    const withLead = (actualLeadCount != null && actualLeadCount > analyticsLeads) ? actualLeadCount : analyticsLeads;
    // If we're adding leads that analytics missed, subtract from the non-lead buckets
    const missingLeads = Math.max(0, withLead - analyticsLeads);
    const droppedNoLead = Math.max(0, (sessionOutcomes.droppedNoLead || 0) - missingLeads);
    data = [
      { name: 'Lead Captured',   value: withLead,                              desc: 'Prospect agreed to be a lead' },
      { name: 'Fully Completed', value: sessionOutcomes.completedNoLead || 0,  desc: 'Script finished, no lead' },
      { name: 'Not Converted',   value: droppedNoLead,                         desc: 'Prospect not interested — normal in cold calling' },
      { name: 'Abandoned',       value: sessionOutcomes.abandoned       || 0,  desc: 'Call ended without reset (app closed, etc.)' },
    ].filter((d) => d.value > 0);
  } else {
    const leadsFromCompleted = Math.max(0, totalLeads || 0);
    const completedNoLead = Math.max(0, (completedCalls || 0) - leadsFromCompleted);
    const dropped = Math.max(0, droppedCalls || 0);
    data = [
      { name: 'Lead Captured',   value: leadsFromCompleted, desc: 'Prospect agreed to be a lead' },
      { name: 'Fully Completed', value: completedNoLead,    desc: 'Script finished, no lead' },
      { name: 'Not Converted',   value: dropped,            desc: 'Prospect not interested — normal in cold calling' },
    ].filter((d) => d.value > 0);
  }
  const total = data.reduce((s, d) => s + d.value, 0);
  const notConvertedPct = total > 0
    ? Math.round(((sessionOutcomes?.droppedNoLead || 0) / total) * 100)
    : 0;

  return (
    <Section
      title="Calls Breakdown"
      icon="🍩"
      loading={loading}
      empty={!loading && total === 0}
      emptyText="No calls yet"
      noPad
    >
      <div className="p-3 pt-1">
        <ResponsiveContainer width="100%" height={chartHeight}>
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius={36}
              outerRadius={54}
              paddingAngle={2}
              dataKey="value"
            >
              {data.map((entry, index) => (
                <Cell key={entry.name} fill={FUNNEL_COLORS[index % FUNNEL_COLORS.length]} />
              ))}
            </Pie>
            <Tooltip
              formatter={(val, name, props) => [
                `${val} (${total > 0 ? Math.round((val / total) * 100) : 0}%)`,
                props?.payload?.desc || name,
              ]}
              contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #E5E7EB' }}
            />
          </PieChart>
        </ResponsiveContainer>
        {/* Legend */}
        <div className="flex flex-col gap-0.5 mt-1">
          {data.map((item, idx) => (
            <div key={item.name} className="flex items-center justify-between text-[11px] leading-tight" title={item.desc}>
              <div className="flex items-center gap-1">
                <span
                  className="w-2 h-2 rounded-full flex-shrink-0"
                  style={{ backgroundColor: FUNNEL_COLORS[idx % FUNNEL_COLORS.length] }}
                />
                <span className="text-gray-600">{item.name}</span>
              </div>
              <span className="font-semibold text-gray-800 tabular-nums">
                {item.value} ({Math.round((item.value / total) * 100)}%)
              </span>
            </div>
          ))}
        </div>
        {/* Cold-calling context note */}
        {notConvertedPct >= 60 && (
          <p className="text-[10px] text-gray-400 mt-1.5 border-t border-gray-100 pt-1.5 leading-tight">
            💡 High "Not Converted" is typical for cold calling (1–5% conversion).
          </p>
        )}
      </div>
    </Section>
  );
};

// ─── Chart: Script Conversion Bar ─────────────────────────────────────────────
const ScriptBarTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-xs">
      <p className="font-semibold text-gray-700">{payload[0].payload.fullName}</p>
      <p className="text-blue-600 font-medium mt-0.5">
        Conversion: {payload[0].value}% · {payload[0].payload.leads} leads / {payload[0].payload.calls} calls
      </p>
    </div>
  );
};

const ScriptConversionChart = ({ scriptRows, loading }) => {
  const chartData = useMemo(
    () =>
      [...scriptRows]
        .sort((a, b) => (b.rate || 0) - (a.rate || 0))
        .slice(0, 7)
        .map((s) => ({
          name: s.name.length > 18 ? s.name.slice(0, 18) + '…' : s.name,
          fullName: s.name,
          rate: s.rate != null ? Math.round(s.rate) : 0,
          leads: s.leads,
          calls: s.calls,
          // Color bars by performance tier
          fill: s.rate >= 35 ? '#10B981' : s.rate >= 20 ? '#F59E0B' : '#EF4444',
        })),
    [scriptRows]
  );

  return (
    <Section
      title="Script Conversion Rates"
      icon="📊"
      loading={loading}
      empty={!loading && chartData.length === 0}
      emptyText="No script data yet — keep making calls and it will appear here"
      noPad
    >
      <div className="p-4 pt-3">
        <ResponsiveContainer width="100%" height={Math.max(120, chartData.length * 34)}>
          <BarChart
            data={chartData}
            layout="vertical"
            margin={{ top: 0, right: 36, left: 4, bottom: 0 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" horizontal={false} />
            <XAxis
              type="number"
              domain={[0, 100]}
              unit="%"
              tick={{ fontSize: 11, fill: '#9CA3AF' }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              type="category"
              dataKey="name"
              width={110}
              tick={{ fontSize: 11, fill: '#374151' }}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip content={<ScriptBarTooltip />} cursor={{ fill: '#F9FAFB' }} />
            <Bar dataKey="rate" radius={[0, 4, 4, 0]} barSize={16}>
              {chartData.map((entry, index) => (
                <Cell key={index} fill={entry.fill} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
        {/* Colour key */}
        <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-500 inline-block" />≥35% Good</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-yellow-400 inline-block" />20–35% Avg</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-400 inline-block" />&lt;20% Low</span>
        </div>
      </div>
    </Section>
  );
};

// ─── Table rows ───────────────────────────────────────────────────────────────

const AgentRow = ({ rank, name, calls, leads, conversion, evalScore }) => {
  const grade = getScoreGrade(evalScore);
  const initials = getInitials(name);
  return (
    <tr className="border-b border-gray-100 last:border-0 hover:bg-gray-50 transition-colors">
      <td className="pl-4 pr-2 py-2.5 w-8">
        <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-xs font-bold ${getRankBadgeClasses(rank)}`}>
          {rank}
        </span>
      </td>
      <td className="px-2 py-2.5">
        <div className="flex items-center gap-2.5">
          <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${getAvatarClasses(rank)}`}>
            {initials}
          </div>
          <span className="text-sm text-gray-800 font-medium truncate max-w-[130px]" title={name}>
            {name}
          </span>
        </div>
      </td>
      <td className="px-2 py-2.5 text-right w-12">
        <span className="text-sm text-gray-700">{calls ?? 0}</span>
      </td>
      <td className="px-2 py-2.5 text-right w-12">
        <span className="text-sm font-bold text-gray-900">{leads}</span>
      </td>
      <td className="px-2 py-2.5 text-right w-16">
        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${getConvClasses(conversion)}`}>
          {conversion}%
        </span>
      </td>
      <td className="pl-2 pr-4 py-2.5 text-right w-20">
        {grade ? (
          <div className="flex flex-col items-end gap-0.5">
            <span className={`text-sm font-bold ${grade.classes.split(' ').find(c => c.startsWith('text-'))}`}>
              {evalScore}
            </span>
            <span className={`text-xs ${grade.classes.split(' ').find(c => c.startsWith('text-'))} opacity-75`}>
              {grade.label}
            </span>
          </div>
        ) : (
          <span className="text-xs text-gray-300">—</span>
        )}
      </td>
    </tr>
  );
};

// Trim auto-generated node ID prefixes for friendlier display
// Resolve actual node label from the template graph (script text or step name)
// Returns the human-readable identifier for a node (step name or formatted ID — NOT script text)
// Extract plain text from a script field that may be a string or a serialized function
const extractScriptText = (value) => {
  if (!value) return '';
  if (typeof value === 'string') {
    const trimmed = value.trim();
    // Skip serialized function strings — they're runtime code, not displayable text
    if (trimmed.startsWith('function') || trimmed.startsWith('(function') || trimmed.startsWith('return ')) return '';
    return trimmed;
  }
  return '';
};

// Resolve a node from the graph using three strategies in order:
// 1. Direct key lookup (fast path — exact node ID match)
// 2. Alias lookup — follows nodeAliases recorded when a node ID was renamed
// 3. Step-name fallback — finds a node whose step/phase name matches the analytics nodeId
const resolveNode = (graph, nodeId) => {
  if (!graph?.nodes || !nodeId) return null;
  // 1. Direct key lookup
  if (graph.nodes[nodeId]) return graph.nodes[nodeId];
  // 2. Alias lookup: nodeAliases maps old IDs → current IDs, recorded on every rename
  const aliasedId = graph.nodeAliases?.[nodeId];
  if (aliasedId && graph.nodes[aliasedId]) return graph.nodes[aliasedId];
  // 3. Step-name fallback (case-insensitive, underscore-tolerant)
  const needle = nodeId.toLowerCase().replace(/_/g, ' ');
  return Object.values(graph.nodes).find(n =>
    n?.step?.toLowerCase().replace(/_/g, ' ') === needle
  ) || null;
};

const getGraphFromTemplate = (tpl) =>
  tpl?.graph || tpl?.data?.scriptGraph || tpl?.scriptGraph || tpl?.data?.graph || null;

const getNodeLabel = (templates, templateId, nodeId) => {
  if (!nodeId) return '—';
  // Always use the node ID as the label — step/phase is a category, not an identifier
  return formatNodeId(nodeId);
};

// Returns the agent script text for a node (what the agent reads aloud, or coaching notes as fallback)
const getNodeScriptText = (templates, templateId, nodeId) => {
  if (!nodeId || !templateId || !templates) return null;
  const tpl = templates[templateId];
  const graph = getGraphFromTemplate(tpl);
  const node = resolveNode(graph, nodeId);
  if (!node) return null;

  const raw = extractScriptText(node.script) || extractScriptText(node.coaching);
  if (!raw) return null;
  return raw.length > 80 ? raw.substring(0, 78) + '…' : raw;
};

const formatNodeId = (nodeId) => {
  if (!nodeId) return '—';
  // Strip common prefixes like 'newNode_', keep user-defined names as-is
  const clean = nodeId.replace(/^newNode_/, 'Node ').replace(/_/g, ' ');
  return clean.length > 18 ? clean.slice(0, 17) + '…' : clean;
};

const ScriptRow = ({ name, leads, calls, rate, completionRate, topDropOff, topDropOffLabel, lastImprovedAt, onImprove }) => {
  const improvedDate = lastImprovedAt
    ? new Date(lastImprovedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    : null;
  return (
  <tr className="border-b border-gray-100 last:border-0 hover:bg-gray-50 transition-colors">
    <td className="pl-4 pr-2 py-2.5">
      <div className="flex flex-col gap-0.5">
        <div className="flex items-center gap-1.5">
          {rate != null && rate < 20 && (
            <span className="text-amber-500 text-xs flex-shrink-0" title="Low conversion">⚠</span>
          )}
          <span className="text-sm font-medium text-gray-800 truncate block max-w-[130px]" title={name}>
            {name}
          </span>
        </div>
        {improvedDate && (
          <span className="text-xs text-emerald-600 font-medium flex items-center gap-1">
            ✨ AI improved {improvedDate}
          </span>
        )}
      </div>
    </td>
    <td className="px-2 py-2.5 text-right w-14">
      <span className="text-sm font-bold text-gray-900">{leads}</span>
    </td>
    <td className="px-2 py-2.5 text-right w-14">
      <span className="text-sm text-gray-400">{calls}</span>
    </td>
    <td className="px-2 py-2.5 text-right w-16"
        title={completionRate != null ? `${completionRate}% of calls reached the end of this script` : 'No data'}>
      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${
        completionRate == null ? 'text-gray-300' :
        completionRate >= 50 ? 'bg-green-100 text-green-700' :
        completionRate >= 20 ? 'bg-yellow-100 text-yellow-700' :
        'bg-red-100 text-red-600'
      }`}>
        {completionRate != null ? `${Math.round(completionRate)}%` : '—'}
      </span>
    </td>
    <td className="px-2 py-2.5 w-24">
      <div className="flex flex-col gap-0.5 items-end">
        <span className={`text-xs font-semibold ${
          rate == null ? 'text-gray-300' :
          rate >= 35 ? 'text-green-600' :
          rate >= 20 ? 'text-amber-600' : 'text-red-500'
        }`}>
          {rate != null ? `${Math.round(rate)}%` : '—'}
        </span>
        <div className="w-full bg-gray-100 rounded-full h-1">
          <div
            className={`h-1 rounded-full ${
              rate == null ? '' :
              rate >= 35 ? 'bg-green-500' :
              rate >= 20 ? 'bg-amber-400' : 'bg-red-400'
            }`}
            style={{ width: `${Math.min(rate || 0, 100)}%` }}
          />
        </div>
      </div>
    </td>
    <td className="pl-2 pr-4 py-2.5 text-right w-20">
      <button
        onClick={onImprove}
        className="text-xs font-semibold text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 px-2.5 py-1 rounded-full transition-colors"
      >
        Improve
      </button>
    </td>
  </tr>
  );
};

// ─── Drop-off Analysis ────────────────────────────────────────────────────────
const DropOffSection = ({ scriptRows, loading, onNavigate, defaultTemplateId }) => {
  const allRows = scriptRows || [];
  const [userSelected, setUserSelected] = React.useState(false);
  const [selectedId, setSelectedId] = React.useState(null);

  const handleSelect = (id) => { setSelectedId(id); setUserSelected(true); };

  // Dropdown shows all scripts; auto-select prefers default, then most calls
  const autoId = React.useMemo(() => {
    if (defaultTemplateId && allRows.find(r => r.id === defaultTemplateId)) return defaultTemplateId;
    const sorted = [...allRows].sort((a, b) => (b.calls || 0) - (a.calls || 0));
    return sorted[0]?.id ?? null;
  }, [defaultTemplateId, allRows]);

  const effectiveId = (userSelected && selectedId && allRows.find(r => r.id === selectedId))
    ? selectedId
    : autoId;

  const selected = allRows.find(r => r.id === effectiveId) ?? null;
  const dropOffs = selected?.allDropOffs || [];
  const maxCount = dropOffs[0]?.count || 1;

  return (
    <Section
      title="Drop-off Analysis"
      icon="📍"
      loading={loading}
      empty={!loading && allRows.length === 0}
      emptyText="No script data for this period"
      action={
        <div className="flex items-center gap-2">
          {allRows.length > 1 && (
            <select
              value={effectiveId || ''}
              onChange={e => handleSelect(e.target.value)}
              className="text-xs text-gray-600 border border-gray-200 rounded px-2 py-0.5 bg-white focus:outline-none focus:ring-1 focus:ring-blue-400 max-w-[160px] truncate"
            >
              {allRows.map(r => (
                <option key={r.id} value={r.id}>{r.name}</option>
              ))}
            </select>
          )}
          {effectiveId && onNavigate && (
            <SectionLink
              label="AI Improve"
              onClick={() => onNavigate('myScripts', `/my-scripts?tab=improve&template=${effectiveId}`)}
            />
          )}
        </div>
      }
    >
      {dropOffs.length === 0 ? (
        <div className="px-4 py-6 text-center text-sm text-gray-400">
          No drop-offs recorded for this script
        </div>
      ) : (
      <div className="px-4 pb-4 pt-2 flex flex-col gap-3">
        {dropOffs.map((node, i) => {
          const pct = selected.calls > 0 ? Math.round((node.count / selected.calls) * 100) : 0;
          const barWidth = Math.round((node.count / maxCount) * 100);
          return (
            <div key={node.nodeId || i} className="flex flex-col gap-1">
              <div className="flex items-start justify-between gap-2">
                <p className="text-sm leading-snug flex-1 line-clamp-2" title={node.script ? `${node.label}: ${node.script}` : node.label}>
                  <span className="font-semibold text-gray-700 text-sm">{node.label}</span>
                  {node.script && (
                    <><span className="text-gray-400 font-normal">: </span>
                    <span className="text-gray-500 italic">&ldquo;{node.script}&rdquo;</span></>
                  )}
                </p>
                <span className="text-xs text-orange-500 font-semibold flex-shrink-0 pt-0.5">
                  {node.count}× &middot; {pct}%
                </span>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-1.5">
                <div
                  className="bg-orange-400 h-1.5 rounded-full transition-all"
                  style={{ width: `${barWidth}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
      )}
    </Section>
  );
};

// ─── Call Outcomes by Hour (Stacked bar chart) ──────────────────────────────
const HOUR_LABELS = [
  '12a','1a','2a','3a','4a','5a','6a','7a','8a','9a','10a','11a',
  '12p','1p','2p','3p','4p','5p','6p','7p','8p','9p','10p','11p',
];

const OUTCOME_COLORS = {
  leadCaptured: '#2563EB',
  completed:    '#10B981',
  notConverted: '#F59E0B',
  abandoned:    '#D1D5DB',
};

const OUTCOME_LABELS = {
  leadCaptured: 'Lead Captured',
  completed:    'Fully Completed',
  notConverted: 'Not Converted',
  abandoned:    'Abandoned',
};

const HourlyOutcomeTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const total = payload.reduce((s, p) => s + (p.value || 0), 0);
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-xs">
      <p className="font-semibold text-gray-700 mb-1">{label} — {total} calls</p>
      {payload.filter(p => p.value > 0).map(p => (
        <p key={p.dataKey} className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full inline-block flex-shrink-0" style={{ backgroundColor: p.fill || p.color }} />
          <span className="text-gray-600">{OUTCOME_LABELS[p.dataKey] || p.dataKey}:</span>
          <span className="font-semibold text-gray-800">{p.value}{total > 0 ? ` (${Math.round((p.value / total) * 100)}%)` : ''}</span>
        </p>
      ))}
    </div>
  );
};

const PeakHoursChart = ({ hourlyOutcomes, loading }) => {
  const data = useMemo(() => {
    if (!hourlyOutcomes || hourlyOutcomes.length !== 24) return [];
    return hourlyOutcomes.map((o, i) => ({
      hour: HOUR_LABELS[i],
      leadCaptured: o.leadCaptured || 0,
      completed: o.completed || 0,
      notConverted: o.notConverted || 0,
      abandoned: o.abandoned || 0,
      _total: (o.leadCaptured || 0) + (o.completed || 0) + (o.notConverted || 0) + (o.abandoned || 0),
    }));
  }, [hourlyOutcomes]);

  const bestHour = useMemo(() => {
    if (!data.length) return null;
    let bestIdx = -1;
    let bestLeads = 0;
    data.forEach((d, i) => {
      if (d.leadCaptured > bestLeads) { bestLeads = d.leadCaptured; bestIdx = i; }
    });
    if (bestLeads > 0) return { label: HOUR_LABELS[bestIdx], detail: `${bestLeads} leads` };
    const maxTotal = Math.max(...data.map(d => d._total));
    if (maxTotal === 0) return null;
    const idx = data.findIndex(d => d._total === maxTotal);
    return { label: HOUR_LABELS[idx], detail: `${maxTotal} calls` };
  }, [data]);

  const isEmpty = !data.length || data.every(d => d._total === 0);

  return (
    <Section
      title="Call Outcomes by Hour"
      icon="🕐"
      loading={loading}
      empty={!loading && isEmpty}
      emptyText="No call activity for this period"
      noPad
      action={bestHour ? (
        <span className="text-[11px] font-semibold text-blue-600">
          Peak: {bestHour.label} ({bestHour.detail})
        </span>
      ) : null}
    >
      <div className="p-3 pt-2 flex flex-col h-full">
        <div className="flex-1 min-h-[120px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 4, right: 4, left: -24, bottom: 0 }} stackOffset="none">
            <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" horizontal vertical={false} />
            <XAxis
              dataKey="hour"
              tick={{ fontSize: 9, fill: '#9CA3AF' }}
              axisLine={false}
              tickLine={false}
              interval={2}
            />
            <YAxis
              tick={{ fontSize: 10, fill: '#9CA3AF' }}
              axisLine={false}
              tickLine={false}
              allowDecimals={false}
            />
            <Tooltip content={<HourlyOutcomeTooltip />} />
            <Bar dataKey="leadCaptured" stackId="a" fill={OUTCOME_COLORS.leadCaptured} barSize={12} />
            <Bar dataKey="completed"    stackId="a" fill={OUTCOME_COLORS.completed} barSize={12} />
            <Bar dataKey="notConverted" stackId="a" fill={OUTCOME_COLORS.notConverted} barSize={12} />
            <Bar dataKey="abandoned"    stackId="a" fill={OUTCOME_COLORS.abandoned} radius={[2, 2, 0, 0]} barSize={12} />
          </BarChart>
        </ResponsiveContainer>
        </div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1 px-1 text-[11px] text-gray-500 flex-shrink-0">
          {Object.entries(OUTCOME_LABELS).map(([key, label]) => (
            <span key={key} className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-sm inline-block" style={{ backgroundColor: OUTCOME_COLORS[key] }} />
              {label}
            </span>
          ))}
        </div>
      </div>
    </Section>
  );
};

const EvalRow = ({ score, date, prospect }) => {
  const grade = getScoreGrade(score);
  const dateStr = date
    ? new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    : '—';
  return (
    <tr className="border-b border-gray-100 last:border-0 hover:bg-gray-50 transition-colors">
      <td className="pl-4 pr-2 py-2.5">
        <span className="text-sm text-gray-700 truncate block max-w-xs" title={prospect}>
          {prospect || 'Unknown prospect'}
        </span>
      </td>
      <td className="px-2 py-2.5 text-right">
        {grade ? (
          <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${grade.classes}`}>
            {score} · {grade.label}
          </span>
        ) : (
          <span className="text-xs text-gray-300">—</span>
        )}
      </td>
      <td className="pl-2 pr-4 py-2.5 text-right w-20">
        <span className="text-xs text-gray-400">{dateStr}</span>
      </td>
    </tr>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

// Cache TTL: 5 minutes
const CACHE_TTL = 5 * 60 * 1000;

const PerformancePage = ({
  analytics,
  userEmail,
  userTeam,
  userTeamName,
  isAdmin,
  isTeamLead,
  userId,
  templates = {},
  defaultTemplateId,
  onNavigate,
}) => {
  const isLeadOrAdmin = isTeamLead || isAdmin;
  const pageTitle = isLeadOrAdmin ? 'Command Center' : 'My Dashboard';

  const [period, setPeriod] = useState('30d');

  const [userNameMap, setUserNameMap] = useState({});
  // true once we have confirmed team membership data (or confirmed no team)
  const [userNamesLoaded, setUserNamesLoaded] = useState(false);
  // true only when fetch succeeded and returned ≥1 member; use as gate for filtering
  const [teamMembersKnown, setTeamMembersKnown] = useState(false);

  // Load templates for the viewed team only. Teams are containerized in Firestore.
  const [teamTemplates, setTeamTemplates] = useState({});
  useEffect(() => {
    if (!userTeam) return;
    const appId = firestorePaths.getArtifactProjectId();
    const teamPath = firestorePaths.getTemplatesPath(appId, userTeam);
    const docToEntry = (d) => {
      const data = d.data();
      const graph = data?.graph || data?.data?.scriptGraph || data?.scriptGraph || data?.data?.graph || null;
      return [d.id, { ...data, graph, name: data?.metadata?.name || data?.name || d.id }];
    };
    getDocs(collection(db, teamPath))
      .then(snap => {
        const map = {};
        snap.forEach(d => { const [id, v] = docToEntry(d); map[id] = v; });
        setTeamTemplates(map);
      })
      .catch(() => {});
  }, [userTeam]);

  // Templates available for drop-off lookup: team-fetched data, with DataContext's
  // normalized versions preferred when available (they have deserialized graph data).
  const mergedTemplates = useMemo(() => {
    const merged = { ...teamTemplates };
    Object.entries(templates).forEach(([id, tpl]) => { if (tpl?.graph) merged[id] = tpl; });
    return merged;
  }, [templates, teamTemplates]);

  const [firestoreLeads, setFirestoreLeads] = useState(null);
  const [kpiData, setKpiData] = useState(null);
  const [evalData, setEvalData] = useState(null);
  const [scriptData, setScriptData] = useState(null);
  const [kpiLoading, setKpiLoading] = useState(true);
  const [evalLoading, setEvalLoading] = useState(true);
  const [scriptLoading, setScriptLoading] = useState(true);

  const [agentKpis, setAgentKpis] = useState(null);
  const [agentEvals, setAgentEvals] = useState([]);
  const [agentScriptData, setAgentScriptData] = useState(null);
  const [agentKpiLoading, setAgentKpiLoading] = useState(true);
  const [agentEvalsLoading, setAgentEvalsLoading] = useState(true);
  const [agentScriptLoading, setAgentScriptLoading] = useState(true);

  // Per-period result cache so switching periods is instant after first load
  const cacheRef = useRef({});
  const getCached = useCallback((key) => {
    const entry = cacheRef.current[key];
    if (!entry) return null;
    if (Date.now() - entry.ts > CACHE_TTL) { delete cacheRef.current[key]; return null; }
    return entry.data;
  }, []);
  const setCache = useCallback((key, data) => {
    cacheRef.current[key] = { data, ts: Date.now() };
  }, []);

  // Always scope analytics to the currently-viewed team.
  // When an admin views a different team, userTeamName already reflects that team,
  // so passing it here ensures analytics queries the correct Firestore path.
  const teamFilter = useMemo(
    () => userTeamName || null,
    [userTeamName]
  );

  // Fetch user names + team membership from Firestore once per team.
  // This is the authoritative list of who actually belongs to this team.
  useEffect(() => {
    if (!userTeam) {
      // No team context (e.g. pure admin with no team) — unblock immediately, no filter
      setUserNamesLoaded(true);
      setTeamMembersKnown(false);
      return;
    }
    let active = true;
    (async () => {
      try {
        const appId = firestorePaths.getArtifactProjectId();
        const snap = await getDocs(collection(db, firestorePaths.getUsersPath(appId, userTeam)));
        if (!active) return;
        const map = {};
        snap.docs.forEach((doc) => {
          const d = doc.data();
          const email = (d.email || '').toLowerCase();
          if (email) map[email] = formatUserName(d);
        });
        setUserNameMap(map);
        // Only enable team-member filtering if we got actual member records
        setTeamMembersKnown(Object.keys(map).length > 0);
        setUserNamesLoaded(true);
      } catch (err) {
        console.error('❌ PerformancePage: user names fetch failed', err);
        // On failure: unblock rendering but don't filter (we don't know who's a member)
        setTeamMembersKnown(false);
        setUserNamesLoaded(true);
      }
    })();
    return () => { active = false; };
  }, [userTeam]);

  const resolveName = useCallback(
    (email) =>
      (email && userNameMap[email.toLowerCase()]) ||
      formatUserName({ userEmail: email }),
    [userNameMap]
  );

  // Team lead fetches — KPI + eval run in parallel; script data comes embedded in KPI
  const fetchTeamData = useCallback(async () => {
    if (!analytics || !isLeadOrAdmin) return;
    const { start, end } = getDateRange(period);

    const kpiKey   = `kpi:${period}:${teamFilter}`;
    const evalKey  = `eval:${period}:${userTeam}`;
    const leadsKey = `leads:${period}:${teamFilter}`;

    // Show cached data immediately (zero wait) while fresh data loads in bg
    const cachedKpi  = getCached(kpiKey);
    const cachedEval = getCached(evalKey);
    const cachedLeads = getCached(leadsKey);

    if (cachedKpi) {
      setKpiData(cachedKpi);
      setScriptData(cachedKpi.scriptPerformance || {});
      setKpiLoading(false);
      setScriptLoading(false);
    }
    if (cachedEval) { setEvalData(cachedEval); setEvalLoading(false); }
    if (cachedLeads) setFirestoreLeads(cachedLeads);

    if (!cachedKpi)  { setKpiLoading(true);  setScriptLoading(true); }
    if (!cachedEval) setEvalLoading(true);

    // Fire KPI + eval + actual leads (full objects) in parallel.
    // Pass userTeam as overrideTeamId so analytics uses the team ID directly,
    // bypassing the name→ID lookup which can fail if team doc names don't match exactly.
    const [kpi, eval_, leadsResult] = await Promise.allSettled([
      cachedKpi  ? Promise.resolve(cachedKpi)  : analytics.getCallAnalytics(teamFilter, start, end, 0, userTeam),
      cachedEval ? Promise.resolve(cachedEval) : analytics.getCallQualityScores(userTeam, start, end),
      cachedLeads ? Promise.resolve(cachedLeads) : analytics.getLeadsData(teamFilter, start, end, 0, userTeam),
    ]);

    if (!cachedKpi) {
      const v = kpi.status === 'fulfilled' ? kpi.value : null;
      const hasKpiData = v && (v.totalCalls > 0 || Object.keys(v.userStats || {}).length > 0 || Object.keys(v.dailyStats || {}).length > 0);
      if (hasKpiData) setCache(kpiKey, v);
      setKpiData(v);
      setKpiLoading(false);
      setScriptData(v?.scriptPerformance || {});
      setScriptLoading(false);
    }
    if (!cachedEval) {
      const v = eval_.status === 'fulfilled' ? eval_.value : null;
      const hasEvalData = v && (Array.isArray(v) ? v.length > 0 : Object.keys(v || {}).length > 0);
      if (hasEvalData) setCache(evalKey, v);
      setEvalData(v);
      setEvalLoading(false);
    }
    if (!cachedLeads) {
      const arr = leadsResult.status === 'fulfilled' ? leadsResult.value : [];
      if (arr && arr.length > 0) setCache(leadsKey, arr);
      setFirestoreLeads(arr || []);
    }
  }, [analytics, isLeadOrAdmin, period, teamFilter, userTeam, getCached, setCache]);

  // Agent fetches — also cached
  const fetchAgentData = useCallback(async () => {
    if (!analytics || isLeadOrAdmin) return;
    const { start, end } = getDateRange(period);

    const kpiKey   = `agentKpi:${period}:${userTeam}`;
    const evalKey  = `agentEval:${period}:${userTeam}:${userEmail}`;
    const leadsKey = `agentLeads:${period}:${teamFilter}`;

    const cachedKpi  = getCached(kpiKey);
    const cachedEval = getCached(evalKey);
    const cachedLeads = getCached(leadsKey);

    if (cachedKpi)  { setAgentKpis(cachedKpi.mine);  setAgentScriptData(cachedKpi.scripts); setAgentKpiLoading(false); setAgentScriptLoading(false); }
    if (cachedEval) { setAgentEvals(cachedEval); setAgentEvalsLoading(false); }
    if (cachedLeads) setFirestoreLeads(cachedLeads);
    if (!cachedKpi)  { setAgentKpiLoading(true); setAgentScriptLoading(true); }
    if (!cachedEval) setAgentEvalsLoading(true);

    const [kpi, eval_, leadsResult] = await Promise.allSettled([
      cachedKpi  ? Promise.resolve(cachedKpi)  : analytics.getCallAnalytics(teamFilter, start, end, 0, userTeam),
      cachedEval ? Promise.resolve(cachedEval) : analytics.getCallQualityScores(userTeam, start, end),
      cachedLeads ? Promise.resolve(cachedLeads) : analytics.getLeadsData(teamFilter, start, end, 0, userTeam),
    ]);

    if (!cachedKpi) {
      const d = kpi.status === 'fulfilled' ? kpi.value : null;
      const mine = d?.userStats
        ? Object.values(d.userStats).find((u) => u.userEmail === userEmail || u.userId === userId)
        : null;
      const scripts = d?.scriptPerformance || null;
      const v = { mine: mine || null, scripts };
      if (mine || scripts) setCache(kpiKey, v);
      setAgentKpis(mine || null);
      setAgentScriptData(scripts);
      setAgentKpiLoading(false);
      setAgentScriptLoading(false);
    }
    if (!cachedEval) {
      const d = eval_.status === 'fulfilled' ? eval_.value : null;
      const v = (d?.recentEvaluations || []).filter((e) => e.salesperson === userEmail);
      if (v.length > 0) setCache(evalKey, v);
      setAgentEvals(v);
      setAgentEvalsLoading(false);
    }
    if (!cachedLeads) {
      const arr = leadsResult.status === 'fulfilled' ? leadsResult.value : [];
      if (arr && arr.length > 0) setCache(leadsKey, arr);
      setFirestoreLeads(arr || []);
    }
  }, [analytics, isLeadOrAdmin, period, teamFilter, userEmail, userId, userTeam, getCached, setCache]);

  useEffect(() => {
    if (isLeadOrAdmin) fetchTeamData();
    else fetchAgentData();
  }, [isLeadOrAdmin, fetchTeamData, fetchAgentData]);

  // Authoritative lead data from Firestore (same source as Leads page).
  const totalLeads = firestoreLeads ? firestoreLeads.length : null;

  // Leads grouped by agent email (for Agent Pulse)
  const leadsByAgent = useMemo(() => {
    if (!firestoreLeads) return {};
    const map = {};
    firestoreLeads.forEach(lead => {
      const email = (lead.userEmail || '').toLowerCase();
      if (email) map[email] = (map[email] || 0) + 1;
    });
    return map;
  }, [firestoreLeads]);

  // Reverse lookup: template name → template ID (for leads that only have templateName)
  const templateNameToId = useMemo(() => {
    const map = {};
    Object.entries(mergedTemplates).forEach(([id, tpl]) => {
      const name = (tpl?.name || tpl?.title || '').toLowerCase();
      if (name) map[name] = id;
    });
    return map;
  }, [mergedTemplates]);

  // Leads grouped by script/template (for Script Performance & Drop-off)
  const leadsByScript = useMemo(() => {
    if (!firestoreLeads) return {};
    const map = {};
    firestoreLeads.forEach(lead => {
      let tmpl = lead.template || '';
      if (!tmpl && lead.templateName) {
        tmpl = templateNameToId[(lead.templateName || '').toLowerCase()] || '';
      }
      if (tmpl) map[tmpl] = (map[tmpl] || 0) + 1;
    });
    return map;
  }, [firestoreLeads, templateNameToId]);

  // Derived: leaderboard rows
  const leaderboardRows = useMemo(() => {
    if (!userNamesLoaded) return [];

    // Build eval scores map from Call Evaluator data
    const evalByEmail = {};
    (evalData?.individualScores || []).forEach((s) => {
      const key = (s.email || '').toLowerCase();
      if (!key) return;
      if (teamMembersKnown && !Object.prototype.hasOwnProperty.call(userNameMap, key)) return;
      evalByEmail[key] = s.avgScore;
    });

    // Build activity map from call analytics events
    const byEmail = {};
    Object.values(kpiData?.userStats || {}).forEach((u) => {
      const email = (u.userEmail || '').toLowerCase();
      if (!email) return;
      if (teamMembersKnown && !Object.prototype.hasOwnProperty.call(userNameMap, email)) return;
      if (!byEmail[email]) {
        byEmail[email] = { email, totalCalls: u.totalCalls || 0, calls: u.callsStarted || 0 };
      } else {
        byEmail[email].totalCalls += u.totalCalls || 0;
        byEmail[email].calls      += u.callsStarted || 0;
      }
    });

    // Also include agents who have eval scores but no analytics events yet —
    // ensures Agent Pulse matches Call Evaluator Performance even when analytics
    // data is sparse or hasn't been generated.
    Object.keys(evalByEmail).forEach((email) => {
      if (!byEmail[email]) {
        byEmail[email] = { email, totalCalls: 0, calls: 0 };
      }
    });

    // Also include agents who have leads (from Firestore) but no analytics events
    Object.keys(leadsByAgent).forEach((email) => {
      const key = email.toLowerCase();
      if (teamMembersKnown && !Object.prototype.hasOwnProperty.call(userNameMap, key)) return;
      if (!byEmail[key]) {
        byEmail[key] = { email: key, totalCalls: 0, calls: 0 };
      }
    });

    if (Object.keys(byEmail).length === 0) return [];

    return Object.values(byEmail)
      .map((u) => {
        const leads = leadsByAgent[u.email] || 0;
        return {
          email: u.email,
          name: resolveName(u.email),
          calls: u.calls,
          leads,
          conversion: u.totalCalls > 0 ? Math.round((leads / u.totalCalls) * 100) : 0,
          evalScore: evalByEmail[u.email] ?? null,
        };
      })
      .sort((a, b) => b.leads - a.leads || b.calls - a.calls || a.name.localeCompare(b.name))
      .slice(0, 10);
  }, [kpiData, evalData, resolveName, userNameMap, userNamesLoaded, teamMembersKnown, leadsByAgent]);

  // Derived: script rows
  const scriptRows = useMemo(() => {
    if (!scriptData) return [];
    return Object.entries(scriptData)
      .filter(([id]) => id && id !== 'unknown-template')
      .map(([id, stats]) => {
        const tpl = mergedTemplates[id];
        const name = tpl?.name || tpl?.title || stats.templateName || id;
        const dropOffSrc = stats.topDropOffNodes?.length
          ? stats.topDropOffNodes
          : (stats.topResetPoints || []);
        const topDropOff = dropOffSrc[0] || null;
        const topDropOffLabel = topDropOff
          ? getNodeLabel(mergedTemplates, id, topDropOff.nodeId)
          : null;
        const leads = leadsByScript[id] || 0;
        return {
          id,
          name,
          leads,
          calls: stats.calls || 0,
          rate: stats.calls > 0 ? Math.round((leads / stats.calls) * 100) : null,
          completionRate: stats.completionRate ?? null,
          topDropOff,
          topDropOffLabel,
          allDropOffs: dropOffSrc
            .filter(n => !!resolveNode(getGraphFromTemplate(mergedTemplates[id]), n.nodeId))
            .map(n => ({
              nodeId: n.nodeId,
              count: n.count,
              label: getNodeLabel(mergedTemplates, id, n.nodeId),
              script: getNodeScriptText(mergedTemplates, id, n.nodeId),
            })),
          lastImprovedAt: tpl?.lastImprovedAt || null,
        };
      })
      .sort((a, b) => b.leads - a.leads)
      .slice(0, 7);
  }, [scriptData, mergedTemplates, leadsByScript]);

  // How many scripts have been AI-improved
  const improvedScriptCount = useMemo(
    () => scriptRows.filter((r) => r.lastImprovedAt).length,
    [scriptRows]
  );

  // Derived: agent script rows (for agent view drop-off analysis)
  const agentScriptRows = useMemo(() => {
    if (!agentScriptData) return [];
    return Object.entries(agentScriptData)
      .filter(([id]) => id && id !== 'unknown-template')
      .map(([id, stats]) => {
        const tpl = mergedTemplates[id];
        const name = tpl?.name || tpl?.title || stats.templateName || id;
        const dropOffSrc = stats.topDropOffNodes?.length
          ? stats.topDropOffNodes
          : (stats.topResetPoints || []);
        return {
          id,
          name,
          leads: leadsByScript[id] || 0,
          calls: stats.calls || 0,
          allDropOffs: dropOffSrc
            .filter(n => !!resolveNode(getGraphFromTemplate(mergedTemplates[id]), n.nodeId))
            .map(n => ({
              nodeId: n.nodeId,
              count: n.count,
              label: getNodeLabel(mergedTemplates, id, n.nodeId),
              script: getNodeScriptText(mergedTemplates, id, n.nodeId),
            })),
        };
      })
      .sort((a, b) => b.calls - a.calls)
      .slice(0, 7);
  }, [agentScriptData, mergedTemplates, leadsByScript]);

  // Derived: trend chart data
  const trendData = useMemo(
    () => buildDailyTrend(kpiData?.dailyStats, period),
    [kpiData, period]
  );

  const avgEvalScore = evalData?.avgScore ?? null;
  const avgGrade = getScoreGrade(avgEvalScore);

  return (
    <div className="flex flex-col h-full overflow-hidden bg-gray-50">
      {/* Page header — matches app-wide pattern */}
      <div className="flex items-center justify-between border-b border-gray-200 px-4 h-14 bg-white flex-shrink-0">
        <h2 className="text-xl font-semibold text-blue-600 border-b-2 border-blue-600 pb-1">{pageTitle}</h2>
        <PeriodSelector period={period} onChange={setPeriod} />
      </div>

      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-4">
        {isLeadOrAdmin ? (
          <TeamLeadView
            kpiData={kpiData}
            kpiLoading={kpiLoading}
            evalLoading={evalLoading}
            scriptLoading={scriptLoading}
            leaderboardRows={leaderboardRows}
            scriptRows={scriptRows}
            trendData={trendData}
            totalLeads={totalLeads}
            avgEvalScore={avgEvalScore}
            avgGrade={avgGrade}
            improvedScriptCount={improvedScriptCount}
            defaultTemplateId={defaultTemplateId}
            onNavigate={onNavigate}
          />
        ) : (
          <AgentView
            agentKpis={agentKpis}
            agentKpiLoading={agentKpiLoading}
            agentEvals={agentEvals}
            agentEvalsLoading={agentEvalsLoading}
            agentScriptRows={agentScriptRows}
            agentScriptLoading={agentScriptLoading}
            defaultTemplateId={defaultTemplateId}
            onNavigate={onNavigate}
            agentLeadCount={firestoreLeads ? leadsByAgent[(userEmail || '').toLowerCase()] || 0 : null}
          />
        )}
        </div>
      </div>
    </div>
  );
};

// ─── Team Lead / Admin View ───────────────────────────────────────────────────

// ─── Shared panel contents (reused in both layouts) ──────────────────────────

const AgentPanelContent = ({ kpiLoading, evalLoading, leaderboardRows, onNavigate }) => {
  const hasEvalScores = leaderboardRows.some((r) => r.evalScore != null);
  return (
  <Section
    title="Agent Pulse"
    icon="👥"
    loading={kpiLoading || evalLoading}
    empty={!kpiLoading && !evalLoading && leaderboardRows.length === 0}
    emptyText="No agent activity"
    noPad
    action={<SectionLink label="Eval Scores" onClick={() => onNavigate?.('callEvaluator', '/ai-call-evaluator?tab=analytics')} />}
  >
    <table className="w-full">
      <thead>
        <tr className="border-b border-gray-100">
          <th className="pl-3 pr-1 py-1.5 w-6 text-left text-[11px] font-semibold text-gray-500 uppercase">Rank</th>
          <th className="px-1 py-1.5 text-left text-[11px] font-semibold text-gray-500 uppercase">Agent</th>
          <th className="px-1 py-1.5 text-right text-[11px] font-semibold text-gray-500 uppercase w-10">Calls</th>
          <th className="px-1 py-1.5 text-right text-[11px] font-semibold text-gray-500 uppercase w-10">Leads</th>
          <th className="px-1 py-1.5 text-right text-[11px] font-semibold text-gray-500 uppercase w-12">Conv%</th>
          {hasEvalScores && (
            <th className="pr-3 pl-1 py-1.5 text-right text-[11px] font-semibold text-gray-500 uppercase w-14">Score</th>
          )}
        </tr>
      </thead>
      <tbody>
        {leaderboardRows.map((row, i) => (
          <tr key={row.email} className="border-b border-gray-50 last:border-0">
            <td className="pl-3 pr-1 py-1.5">
              <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold ${getRankBadgeClasses(i + 1)}`}>{i + 1}</span>
            </td>
            <td className="px-1 py-1.5 text-sm text-gray-800 truncate max-w-[90px]">{row.name}</td>
            <td className="px-1 py-1.5 text-right text-xs text-gray-600 tabular-nums">{row.calls}</td>
            <td className="px-1 py-1.5 text-right text-xs font-semibold text-blue-600 tabular-nums">{row.leads}</td>
            <td className="px-1 py-1.5 text-right text-xs tabular-nums">
              <span className={`${getConvClasses(row.conversion)} px-1.5 py-0.5 rounded text-[10px] font-semibold`}>{row.conversion}%</span>
            </td>
            {hasEvalScores && (
              <td className="pr-3 pl-1 py-1.5 text-right text-xs tabular-nums">
                {row.evalScore != null ? (
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                    row.evalScore >= 85 ? 'bg-green-50 text-green-700' :
                    row.evalScore >= 70 ? 'bg-blue-50 text-blue-700' :
                    row.evalScore >= 55 ? 'bg-yellow-50 text-yellow-700' :
                    'bg-red-50 text-red-700'
                  }`}>{row.evalScore}</span>
                ) : (
                  <span className="text-gray-300 text-[10px]">—</span>
                )}
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  </Section>
  );
};

const ScriptPanelContent = ({ scriptLoading, scriptRows, improvedScriptCount, onNavigate }) => (
  <Section
    title="Script Health"
    icon="📋"
    loading={scriptLoading}
    empty={!scriptLoading && scriptRows.length === 0}
    emptyText="No script activity for this period"
    noPad
    action={
      <div className="flex items-center gap-2">
        {improvedScriptCount > 0 && (
          <span className="flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">
            ✨ {improvedScriptCount} AI improved
          </span>
        )}
        <SectionLink label="All scripts" onClick={() => onNavigate?.('myScripts')} />
      </div>
    }
  >
    <table className="w-full">
      <thead>
        <tr className="border-b border-gray-100">
          <th className="pl-4 pr-2 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Script</th>
          <th className="px-2 py-2 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide w-14">Leads</th>
          <th className="px-2 py-2 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide w-14">Calls</th>
          <th className="px-2 py-2 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide w-16" title="% of calls that reached the end of the script">End%</th>
          <th className="px-2 py-2 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide w-24">Lead%</th>
          <th className="pl-2 pr-4 py-2 w-20" />
        </tr>
      </thead>
      <tbody>
        {scriptRows.map((row) => (
          <ScriptRow
            key={row.id}
            name={row.name}
            leads={row.leads}
            calls={row.calls}
            rate={row.rate}
            completionRate={row.completionRate}
            topDropOff={row.topDropOff}
            topDropOffLabel={row.topDropOffLabel}
            lastImprovedAt={row.lastImprovedAt}
            onImprove={() => onNavigate?.('myScripts', `/my-scripts?tab=improve&template=${row.id}`)}
          />
        ))}
      </tbody>
    </table>
  </Section>
);

// ─── KPI grid (compact 2×2) ───────────────────────────────────────────────────
const KpiGrid = ({ kpiLoading, kpiData, totalLeads, avgEvalScore, avgGrade }) => (
  <div className="grid grid-cols-2 grid-rows-2 gap-2.5 h-full">
    {kpiLoading ? (
      [1, 2, 3, 4].map((i) => <StatCardSkeleton key={i} />)
    ) : (
      <>
        <KpiCard icon="🎯" label="Leads Generated" value={totalLeads ?? 0} theme="blue" />
        <KpiCard icon="📞" label="Calls Made" value={kpiData?.totalCallsStarted ?? 0} theme="indigo" />
        <KpiCard
          icon="📈"
          label="Conversion Rate"
          theme="emerald"
          value={totalLeads != null && kpiData?.totalCallsStarted > 0
            ? `${(totalLeads / kpiData.totalCallsStarted * 100).toFixed(1)}%`
            : '—'}
        />
        <KpiCard
          icon="⭐"
          label="Eval Score"
          theme="amber"
          value={avgEvalScore != null ? `${avgEvalScore}/100` : '—'}
          sub={avgGrade ? avgGrade.label : null}
        />
      </>
    )}
  </div>
);

const TeamLeadView = ({
  kpiData,
  kpiLoading,
  evalLoading,
  scriptLoading,
  leaderboardRows,
  scriptRows,
  trendData,
  totalLeads,
  avgEvalScore,
  avgGrade,
  improvedScriptCount,
  defaultTemplateId,
  onNavigate,
}) => {
  const { trendH, funnelH } = useChartHeights();

  return (
    <div className="space-y-3">
      {/* Row 1: KPIs (compact 2×2) + Trend chart */}
      <div className="grid grid-cols-12 gap-3">
        <div className="col-span-4">
          <KpiGrid kpiLoading={kpiLoading} kpiData={kpiData} totalLeads={totalLeads} avgEvalScore={avgEvalScore} avgGrade={avgGrade} />
        </div>
        <div className="col-span-8">
          <TrendChart data={trendData} loading={kpiLoading} chartHeight={trendH} />
        </div>
      </div>
      {/* Row 2: Calls Breakdown + Agent Pulse + Script Health */}
      <div className="grid grid-cols-12 gap-3">
        <div className="col-span-3">
          <FunnelChart
            sessionOutcomes={kpiData?.sessionOutcomes}
            completedCalls={kpiData?.completedCalls}
            totalLeads={totalLeads}
            droppedCalls={kpiData?.resetCalls}
            loading={kpiLoading}
            chartHeight={funnelH}
            actualLeadCount={totalLeads}
          />
        </div>
        <div className="col-span-4">
          <AgentPanelContent kpiLoading={kpiLoading} evalLoading={evalLoading} leaderboardRows={leaderboardRows} onNavigate={onNavigate} />
        </div>
        <div className="col-span-5">
          <ScriptPanelContent scriptLoading={scriptLoading} scriptRows={scriptRows} improvedScriptCount={improvedScriptCount} onNavigate={onNavigate} />
        </div>
      </div>
      {/* Row 3: Drop-off analysis + Peak calling hours */}
      <div className="grid grid-cols-12 gap-3">
        <div className="col-span-5">
          <DropOffSection scriptRows={scriptRows} loading={scriptLoading} onNavigate={onNavigate} defaultTemplateId={defaultTemplateId} />
        </div>
        <div className="col-span-7">
          <PeakHoursChart hourlyOutcomes={kpiData?.hourlyOutcomes} loading={kpiLoading} />
        </div>
      </div>
    </div>
  );
};

// ─── Agent View ───────────────────────────────────────────────────────────────

const AgentView = ({ agentKpis, agentKpiLoading, agentEvals, agentEvalsLoading, agentScriptRows, agentScriptLoading, defaultTemplateId, onNavigate, agentLeadCount }) => {
  const myLeads = agentLeadCount ?? (agentKpis?.leads || 0);
  const convRate =
    agentKpis?.totalCalls > 0
      ? Math.round((myLeads / agentKpis.totalCalls) * 100)
      : 0;

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-4 gap-3">
        {agentKpiLoading ? (
          [1, 2, 3, 4].map((i) => <StatCardSkeleton key={i} />)
        ) : (
          <>
            <KpiCard icon="📞" label="My Calls" value={agentKpis?.totalCalls ?? 0} theme="indigo" />
            <KpiCard icon="🎯" label="My Leads" value={myLeads} theme="blue" />
            <KpiCard icon="📈" label="My Conversion" value={`${convRate}%`} theme="emerald" />
            <KpiCard
              icon="⏱️"
              label="Avg Duration"
              theme="amber"
              value={agentKpis?.avgCallDuration != null ? `${agentKpis.avgCallDuration.toFixed(1)}m` : '—'}
            />
          </>
        )}
      </div>

      <div className="grid grid-cols-12 gap-3">
        <div className="col-span-7">
          <Section
            title="My Recent Evaluations"
            icon="🎙️"
            loading={agentEvalsLoading}
            empty={!agentEvalsLoading && agentEvals.length === 0}
            emptyText="No evaluations yet — upload a call recording to get started."
            noPad
            action={<SectionLink label="View all" onClick={() => onNavigate?.('callEvaluator')} />}
          >
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="pl-4 pr-2 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Prospect</th>
                  <th className="px-2 py-2 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide w-36">Score</th>
                  <th className="pl-2 pr-4 py-2 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide w-20">Date</th>
                </tr>
              </thead>
              <tbody>
                {agentEvals.slice(0, 5).map((ev) => (
                  <EvalRow key={ev.id} score={ev.score} date={ev.date} prospect={ev.prospect} />
                ))}
              </tbody>
            </table>
          </Section>
        </div>
        <div className="col-span-5">
          <DropOffSection scriptRows={agentScriptRows || []} loading={agentScriptLoading} onNavigate={onNavigate} defaultTemplateId={defaultTemplateId} />
        </div>
      </div>
    </div>
  );
};

export default PerformancePage;
